/**
 * URL resolution utilities for derived assets
 *
 * The frontend constructs asset URLs from the CDN domain (returned by the
 * signed-cookies API) and relies on browser-attached HttpOnly cookies scoped
 * to the shared parent domain (e.g. `.stud1o.net`) to authorise the request.
 *
 * No signature material ever appears in URL query strings — CloudFront
 * validates the `CloudFront-Policy`, `CloudFront-Signature`, and
 * `CloudFront-Key-Pair-Id` cookies on each request.
 *
 * Flow:
 *  1. Fetch `POST /api/v1/projects/:projectId/signed-cookies` once per project.
 *     The server responds with three `Set-Cookie` headers (scoped to
 *     `Path=/projects/{projectId}/`) and a JSON body of
 *     `{ domain, expiresAt, ttlSeconds, projectPrefix }`.
 *  2. Use the returned `domain` + locator rule `pathTemplate` to build the URL.
 *  3. `<video>` / `<img>` / `fetch(..., { credentials: 'include' })` pick up
 *     the cookies automatically since app and CDN share a registrable domain.
 */

export interface CloudFrontAuth {
  /** CDN host (e.g. `assets.stud1o.net`) */
  domain: string;
  /** ISO timestamp when the signed cookies expire */
  expiresAt: string;
  /** Cookie TTL in seconds — used to derive frontend refresh interval */
  ttlSeconds: number;
}

export interface ResolveDerivedUrlParams {
  projectId: string;
  assetId: string;
  pathTemplate: string;
  params: Record<string, string>;
  index?: number;
  /** Provides the CDN domain for URL construction. */
  auth?: CloudFrontAuth | null;
}

/**
 * Resolve a locator rule template into a full CDN URL.
 *
 * Browser cookies attach automatically on request — no query-string auth.
 *
 * @example
 * resolveDerivedAssetUrl({
 *   projectId: 'proj-456',
 *   assetId: 'abc-123',
 *   pathTemplate: 'chunks/{resolution}/chunk_{index}.mp4',
 *   params: { resolution: '720p' },
 *   index: 42,
 *   auth: { domain: 'assets.stud1o.net', expiresAt: '...' },
 * })
 */
export function resolveDerivedAssetUrl(p: ResolveDerivedUrlParams): string {
  const domain = p.auth?.domain ?? import.meta.env.VITE_CDN_BASE_URL;
  const base = `https://${domain}/projects/${p.projectId}/${p.assetId}`;
  let path = p.pathTemplate;

  for (const [key, value] of Object.entries(p.params)) {
    path = path.replace(`{${key}}`, value);
  }

  if (p.index !== undefined) {
    path = path.replace("{index}", String(p.index).padStart(6, "0"));
  }

  return `${base}/${path}`;
}

/**
 * Normalise a persisted CloudFront URL so it uses the current CDN host and
 * carries no legacy signature query parameters.
 *
 * Older persisted URLs (pre-cookie-mode) embedded `Policy`, `Signature`, and
 * `Key-Pair-Id` in the query string. In cookie mode those values are no
 * longer valid (and leak signature material into logs), so we strip them.
 * Host rewrites are kept so stale `*.cloudfront.net` URLs migrate to the
 * branded `assets.<env>` domain.
 */
export function refreshCloudFrontUrl(
  url: string,
  auth?: CloudFrontAuth | null,
): string {
  if (!auth) return url;

  try {
    const parsed = new URL(url);

    // Only rewrite CloudFront / assets URLs — leave everything else alone.
    if (
      !parsed.hostname.includes("cloudfront.net") &&
      parsed.hostname !== auth.domain
    ) {
      return url;
    }

    const refreshed = new URL(parsed.pathname, `https://${auth.domain}`);
    // Preserve any non-auth query params (rare, but e.g. debug flags)
    for (const [key, value] of parsed.searchParams) {
      if (
        key === "Policy" ||
        key === "Signature" ||
        key === "Key-Pair-Id" ||
        key === "Expires"
      ) {
        continue;
      }
      refreshed.searchParams.set(key, value);
    }
    return refreshed.toString();
  } catch {
    // If URL parsing fails, return original
    return url;
  }
}

/**
 * Whether it is safe to guess the thumbnail path instead of waiting for the
 * derived-asset record.
 *
 * The orchestrator writes every auto-thumbnail to
 * `projects/{projectId}/{assetId}/thumbnail.jpg` - `post-action-rules.ts` uses
 * `ruleKey: 'thumbnail_v1'` with `format: 'jpg'` for both video and image node
 * outputs - so the path is predictable before the record exists. That closes the
 * gap between an asset appearing and its thumbnail record being fetched.
 *
 * It is only safe when NO `thumbnail_v1` record exists yet. Once a record is
 * present but not `complete`, the object is known not to be uploaded, so a
 * guessed URL is a guaranteed 404 - and browsers cache those, meaning the
 * thumbnail stays broken after it becomes available. In that window callers
 * should fall through to the API's presigned `thumbnailUrl` instead.
 *
 * Audio and documents never get a `thumbnail.jpg`, so they are excluded.
 */
export function canGuessThumbnailPath(asset: {
  mimeType?: string;
  derivedAssets?: Array<{ ruleKey?: string; status?: string }>;
}): boolean {
  const isVisualAsset =
    asset.mimeType?.startsWith("video/") ||
    asset.mimeType?.startsWith("image/");

  if (!isVisualAsset) return false;

  // A record in any state means the job is known about - do not guess.
  const hasThumbnailRecord = asset.derivedAssets?.some(
    (da) => da.ruleKey === "thumbnail_v1",
  );

  return !hasThumbnailRecord;
}

/**
 * Resolve the best available thumbnail URL for an asset.
 *
 * Priority chain:
 *  1. Derived asset `thumbnail_v1` with `status === "complete"` + CDN auth → CDN URL
 *  2. Guessed `thumbnail.jpg` CDN path, when auth is present and no thumbnail
 *     record exists yet - see {@link canGuessThumbnailPath}
 *  3. API-provided presigned `thumbnailUrl` (also covers a thumbnail still
 *     being generated, and the window before auth loads)
 *  4. `undefined` (no thumbnail available)
 */
export function resolveThumbnailUrl(
  asset: {
    id: string;
    mimeType?: string;
    thumbnailUrl?: string;
    derivedAssets?: Array<{
      ruleKey?: string;
      status?: string;
      pathTemplate?: string;
      params?: Record<string, unknown>;
    }>;
  },
  projectId: string,
  auth?: CloudFrontAuth | null,
): string | undefined {
  // Priority 1: derived asset thumbnail resolved via locator rule
  const thumb = asset.derivedAssets?.find(
    (da) => da.ruleKey === "thumbnail_v1" && da.status === "complete",
  );

  if (thumb?.pathTemplate && auth) {
    return resolveDerivedAssetUrl({
      projectId,
      assetId: asset.id,
      pathTemplate: thumb.pathTemplate,
      params: (thumb.params ?? {}) as Record<string, string>,
      auth,
    });
  }

  // Priority 2: guess the thumbnail path before the record arrives
  if (auth && canGuessThumbnailPath(asset)) {
    return resolveDerivedAssetUrl({
      projectId,
      assetId: asset.id,
      pathTemplate: "thumbnail.jpg",
      params: {},
      auth,
    });
  }

  // Priority 3: API presigned thumbnailUrl
  return asset.thumbnailUrl;
}
